import type {
  LaunchWorkflowId,
  WorkflowLaunchInputs,
} from "schemas/generated/workflowLaunch";
import type { LaunchStep } from "schemas/workflowLaunchDefinition";

import * as React from "react";

import { resolveLaunchSteps } from "schemas/workflowLaunchDefinition";

import { Button } from "../../components/button/Button";
import { Input } from "../../components/input/Input";
import { WorkflowStep } from "./WorkflowStep";

export function WorkflowLaunchChecklist({
  workflowId,
  inputs,
  onChange,
  projectId,
  shopifyConnected,
  productAvailable,
  productPicker,
  onContinue,
  busy,
  initialStep,
  error,
  hasSubmission = false,
  readOnly = false,
  hideContinue = false,
}: {
  workflowId: LaunchWorkflowId;
  inputs: WorkflowLaunchInputs;
  onChange: (inputs: WorkflowLaunchInputs) => void;
  projectId: string | null;
  shopifyConnected: boolean;
  productAvailable: boolean;
  productPicker?: React.ReactNode;
  onContinue: () => void;
  busy: boolean;
  initialStep?: LaunchStep["id"] | null;
  error?: string | null;
  hasSubmission?: boolean;
  readOnly?: boolean;
  hideContinue?: boolean;
}) {
  let continueLabel = projectId ? "Build workflow" : "Continue in Replo";
  if (hasSubmission) {
    continueLabel = "Open conversation";
  }
  if (busy) {
    continueLabel = "Saving…";
  }
  const resolved = resolveLaunchSteps({
    workflowId,
    inputs,
    projectId,
    shopifyConnected,
    productAvailable,
  });
  const hasUserProvidedInput = Boolean(
    inputs.sourceUrl.trim() ||
      inputs.productDescription.trim() ||
      inputs.productId,
  );
  const [open, setOpen] = React.useState<string | null>(
    initialStep ??
      (hasUserProvidedInput
        ? resolved.steps.find((step) => step.state !== "complete")?.id ?? null
        : null),
  );
  const stepRefs = React.useRef(new Map<string, HTMLDivElement>());
  return (
    <div className="flex min-w-0 w-full flex-col gap-5">
      <div>
        {resolved.steps.map((step, index) => {
          const requiresAccount = readOnly && step.requires.includes("project");
          return (
            <WorkflowStep
              key={step.id}
              label={requiresAccount ? `${step.title} in Replo` : step.title}
              stepNumber={index + 1}
              done={step.state === "complete"}
              open={
                readOnly
                  ? Boolean(inputs[step.field]?.trim()) &&
                    (step.field !== "conversionGoal" ||
                      inputs.conversionGoal !== "Add to cart")
                  : open === step.id
              }
              onOpenChange={(expanded) => setOpen(expanded ? step.id : null)}
            >
              <div
                ref={(element) => {
                  if (element) {
                    stepRefs.current.set(step.id, element);
                  } else {
                    stepRefs.current.delete(step.id);
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    event.key !== "Tab" ||
                    event.defaultPrevented ||
                    event.altKey ||
                    event.ctrlKey ||
                    event.metaKey ||
                    readOnly ||
                    busy ||
                    hasSubmission
                  ) {
                    return;
                  }
                  const controls = getStepControls(event.currentTarget);
                  const boundary = event.shiftKey
                    ? controls.at(0)
                    : controls.at(-1);
                  if (event.target !== boundary) {
                    return;
                  }
                  const adjacent =
                    resolved.steps[index + (event.shiftKey ? -1 : 1)];
                  if (!adjacent) {
                    return;
                  }
                  event.preventDefault();
                  setOpen(adjacent.id);
                  requestAnimationFrame(() => {
                    const body = stepRefs.current.get(adjacent.id);
                    if (!body) {
                      return;
                    }
                    const targets = getStepControls(body);
                    const target = event.shiftKey
                      ? targets.at(-1)
                      : targets.at(0);
                    // An unconnected integration may only have explanatory text.
                    (
                      target ??
                      body
                        .closest('[data-slot="collapsible"]')
                        ?.querySelector<HTMLButtonElement>(
                          '[data-slot="collapsible-trigger"]',
                        )
                    )?.focus();
                  });
                }}
              >
                {step.integration ? (
                  productPicker ??
                  (!requiresAccount && (
                    <p className="text-sm text-muted">
                      Continue in Replo to select a project and connect Shopify.
                    </p>
                  ))
                ) : (
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="sr-only">{step.title}</span>
                    <Input
                      value={inputs[step.field] ?? ""}
                      disabled={busy || hasSubmission}
                      readOnly={readOnly}
                      inputMode={step.field === "sourceUrl" ? "url" : undefined}
                      autoCapitalize={
                        step.field === "sourceUrl" ? "none" : undefined
                      }
                      spellCheck={
                        step.field === "sourceUrl" ? false : undefined
                      }
                      onChange={(event) => {
                        return onChange({
                          ...inputs,
                          [step.field]: event.target.value,
                        });
                      }}
                      placeholder={
                        step.field === "sourceUrl" ? "example.com" : undefined
                      }
                    />
                    {step.id === "source" &&
                      Boolean(inputs.sourceUrl) &&
                      step.state !== "complete" && (
                        <span className="text-danger">
                          Enter a web address like example.com.
                        </span>
                      )}
                  </label>
                )}
              </div>
            </WorkflowStep>
          );
        })}
      </div>
      {error && (
        <p
          role="alert"
          className="text-sm text-danger [overflow-wrap:anywhere]"
        >
          {error}
        </p>
      )}
      {!hideContinue && (
        <div className="flex border-t-0.5 border-border pt-5">
          <Button
            size="lg"
            className="w-full"
            disabled={
              readOnly ||
              busy ||
              Boolean(projectId && !resolved.ready && !hasSubmission)
            }
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

function getStepControls(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      "input, textarea, select, button, a[href], [tabindex]",
    ),
  ).filter((element) => {
    return (
      element.tabIndex >= 0 &&
      !element.matches(':disabled, [aria-disabled="true"]') &&
      !element.closest("[hidden], [inert]") &&
      element.getClientRects().length > 0
    );
  });
}
